import api from 'api';
import Button from 'components/button';
import Callout from 'components/callout';
import Controls from 'components/controls';
import Modal from 'components/modal';
import { CONTACT_INFO_REGEX, UserType } from 'config';
import AuthenticationModal from 'containers/authentication';
import { MessageIcon } from 'icons';
import { enqueueSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getUserType } from 'state/selectors';
import { APP_ROUTES, SHOW_HELP } from 'utils/routing';

interface Instructor {
  userId: number;
  firstName: string;
  segments: string[];
}

interface AppointmentProduct {
  id: string;
  price: number;
}

const MessageButton = ({
  instructor,
  appointmentProduct = undefined,
  className = '',
  variant = 'icon',
}: {
  instructor: Instructor;
  appointmentProduct?: AppointmentProduct;
  className?: string;
  variant?: 'full' | 'icon';
}) => {
  const userType = useSelector(getUserType);
  const [showModal, setShowModal] = useState(false);

  if (userType !== UserType.Anonymous && userType !== UserType.Client) {
    return null;
  }

  return (
    <>
      {variant === 'full' ? (
        <Button
          className={className}
          variant="contained"
          onClick={() => setShowModal(true)}
          size="small"
        >
          Send message
        </Button>
      ) : (
        <button
          className={`btn-icon gray shrink-0 text-blue-600 border border-gray-300${cls(
            className
          )}`}
          onClick={() => setShowModal(true)}
        >
          <MessageIcon className="text-gray-600" width={24} />
        </button>
      )}
      {userType === UserType.Anonymous ? (
        <AuthenticationModal
          title="Log in or sign up to message"
          flow="MessagesFlow"
          initialForm="login"
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          defaultUserType={UserType.Client}
        />
      ) : (
        <MessageInstructorModal
          instructor={instructor}
          appointmentProduct={appointmentProduct}
          open={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
};

enum Step {
  Faq = 1,
  Message = 2,
  Sent = 3,
}

const MessageInstructorModal = ({
  instructor,
  appointmentProduct,
  open,
  onClose,
}: {
  instructor: Instructor;
  appointmentProduct?: AppointmentProduct;
  open: boolean;
  onClose: () => void;
}): JSX.Element => {
  const [step, setStep] = useState(Step.Faq);

  let title;
  if (step === Step.Faq) {
    title = 'Most ask about';
  } else if (step === Step.Message) {
    title = 'Message ' + instructor.firstName.capitalize();
  } else {
    title = 'Message sent!';
  }

  const handleClose = () => {
    onClose();
    setStep(Step.Faq);
  };

  const handleNext = () => {
    const newStep = (step + 1) % 3;
    if (newStep === 1) {
      onClose();
    }
    setStep(newStep);
  };

  return (
    <Modal
      name="Message instructor"
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      title={title}
    >
      {step === Step.Faq ? (
        <FaqModalStep
          onNext={handleNext}
          instructor={instructor}
          appointmentProduct={appointmentProduct}
        />
      ) : step === Step.Message ? (
        <MessageModalStep
          onNext={handleNext}
          instructor={instructor}
          appointmentProduct={appointmentProduct}
        />
      ) : (
        <SentModalStep
          onNext={handleNext}
          instructor={instructor}
          appointmentProduct={appointmentProduct}
        />
      )}
    </Modal>
  );
};

interface ModalStep {
  onNext(): void;
  instructor: Instructor;
  appointmentProduct?: AppointmentProduct;
}

const FaqModalStep = (props: ModalStep) => {
  const ulClasses = 'list-disc pl-6 mb-3 mt-1';

  return (
    <div>
      <h4 className="font-normal text-md">{props.instructor.firstName}</h4>
      <ul className={ulClasses}>
        <li>Teaches {props.instructor.segments.join(', ').toLowerCase()}</li>
        {!!props.appointmentProduct && (
          <li>${props.appointmentProduct.price} per lesson</li>
        )}
      </ul>
      <h4 className="font-normal text-md">Lesson bookings</h4>
      <ul className={ulClasses}>
        <li>Each 1 hour, up to 2 swimmers</li>
        <li>Free cancellation up to 2 days before lesson start</li>
      </ul>
      <p className="text-center mt-6">
        For general inquiries, visit{' '}
        <span
          className="underline hover:cursor-pointer"
          onClick={SHOW_HELP.ROOT}
        >
          our help center
        </span>
      </p>
      <hr />
      <Controls className="!py-0">
        <div className="w-full flex gap-4 justify-end items-center">
          <span>Still have a question?</span>
          <Button onClick={props.onNext} variant="contained" color="primary">
            Message {props.instructor.firstName}
          </Button>
        </div>
      </Controls>
    </div>
  );
};

const MessageModalStep = (props: ModalStep) => {
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSend = async () => {
    setIsLoading(true);
    const censoredMessage = message.replace(CONTACT_INFO_REGEX, '*********');
    try {
      await api.conversations.create({
        userId: props.instructor.userId,
        message: censoredMessage,
      });
      props.onNext();
      setMessage('');
    } catch (err) {
      enqueueSnackbar({ message: 'Error sending message', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <div className="mb-4">
        <Callout className="mb-4">
          Keep all communication on platform. Never share contact information
          with an instructor.
        </Callout>
        <p>I'm looking for...</p>
        <textarea
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          placeholder="weekday lessons, hoping to learn front crawl, etc."
        />
      </div>
      <Controls className="!pb-0">
        <Button
          isLoading={isLoading}
          onClick={async () => await handleSend()}
          variant="contained"
          color="primary"
        >
          Send
        </Button>
      </Controls>
    </div>
  );
};

const SentModalStep = (props: ModalStep) => {
  const history = useHistory();
  return (
    <div>
      <p className="mt-2 mb-4">We'll notify you when they respond.</p>
      <Controls className="!pb-0">
        <Button onClick={props.onNext} variant="contained">
          Back to profile
        </Button>
        {!!props.appointmentProduct && (
          <Button
            onClick={() =>
              history.push(
                APP_ROUTES.BOOK.nav(props.appointmentProduct.id, 'datetime')
              )
            }
            variant="contained"
            color="primary"
          >
            View availability
          </Button>
        )}
      </Controls>
    </div>
  );
};

export default MessageButton;
